import "react-sliding-pane/dist/react-sliding-pane.css";

import { useEffect, useState } from "react";

import AccessGroupList from "../access-group/AccessGroup";
import Card from "shared/components/data-display/card/Card";
import CustomErrors from "shared/constants/errorMessages";
import Button from "shared/components/inputs/mui/Button";
import Loader from "shared/components/feedback/loading/Loader";
import More from "./More";
import PropTypes from "prop-types";
import Text from "shared/components/data-display/mui/Text";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import env from "react-dotenv";
import { errorNotification } from "shared/components/feedback/notification/notification";
import moment from "moment";
import { useAllocationsVisibilityOfUser } from "features/allocations/hooks/useAllocationsPermission";
import { useLocation } from "react-router-dom";
import { useStylesForText } from "shared/styles/makeStyles";
import { useUser } from "shared/services/api/useUser";
import {
    Container,
    Body,
    EmployeePicture,
    Title,
    Tag,
    ButtonLink,
    TextLink,
    Footer,
    MoreMenuContainer,
    Section,
    GroupSection,
    SkillSearchContainer,
    LabelText,
    TitleText,
} from "./EmployeeDrawer.styles";

const EmployeeDrawer = (props) => {
    const location = useLocation();
    const classesText = useStylesForText();
    const { userId } = props;
    const canViewAllocations = useAllocationsVisibilityOfUser(userId);
    const { getUserDetail } = useUser();
    const [profileLink, setProfileLink] = useState(null);
    const [user, setUser] = useState({
        id: null,
        name: null,
        title: null,
        photo: null,
        type: null,
        department: null,
        departmentId: null,
        location: null,
        talentManager: null,
        departureDate: null,
        community: null,
    });

    useEffect(() => {

        const fetchUserData = async () => {
            if (userId) {
                try {
                    const fetchedUser = await getUserDetail([{ name: "$userId", value: userId }]);

                    setUser({
                        id: userId,
                        name: fetchedUser.FullName,
                        title: fetchedUser.Title,
                        photo:
                            fetchedUser.LargePhotoUrl &&
                            `${env.BESTWORK_REACT_ASSET_DOMAIN}/${fetchedUser.LargePhotoUrl}`,
                        type: fetchedUser.EmploymentType,
                        department: fetchedUser.Department,
                        departmentId: fetchedUser.DepartmentId,
                        seatOffice: fetchedUser.Office,
                        payOffice: fetchedUser.PayOffice,
                        talentManager: fetchedUser.TalentManager,
                        departureDate: fetchedUser.DepartureDate,
                        community: fetchedUser.Community,
                    });

                    const url = null;
                    setProfileLink(url);
                } catch {
                    // Error silently
                }
            } else {
                errorNotification(undefined, CustomErrors.OnGetUser);
            }
        };

        fetchUserData();
    }, [userId, getUserDetail]);

    return (
        <Container>
            <Body canViewAllocations={canViewAllocations}>
                {userId && user.name ? (
                    <>
                        <EmployeePicture url={user.photo} />
                        <Title>
                            <LabelText variant="xxxlarge">
                                {user.name}
                            </LabelText>
                            <MoreMenuContainer>
                                <More
                                    visible
                                    disableProfile
                                    employee={user}
                                    skillSearchLink={profileLink}
                                    direction="left"
                                    portal={true}
                                />
                            </MoreMenuContainer>
                        </Title>
                        {user.type === "Freelancer" && (
                            <Tag>
                                <Text variant="xxsmallMedium">
                                    FREELANCE
                                </Text>
                            </Tag>
                        )}
                        <GroupSection>
                            <Section>
                                <Text
                                    variant="xsmallLight"
                                    className={classesText.gray2}
                                >
                                    Title
                                </Text>
                                <TitleText variant="small">
                                    {user.title}
                                </TitleText>
                            </Section>

                            <Section>
                                <Text
                                    variant="xsmallLight"
                                    className={classesText.gray2}
                                >
                                    Department
                                </Text>
                                <TitleText variant="small">
                                    {user.department}
                                </TitleText>
                            </Section>
                        </GroupSection>
                        <GroupSection>
                            <Section>
                                <Text
                                    variant="xsmallLight"
                                    className={classesText.gray2}
                                >
                                    Pay Office
                                </Text>
                                <TitleText variant="small">
                                    {user.payOffice}
                                </TitleText>
                            </Section>
                            <Section>
                                <Text
                                    variant="xsmallLight"
                                    className={classesText.gray2}
                                >
                                    Community / Group
                                </Text>
                                <TitleText variant="small">
                                    {user.community ? (
                                        user.community
                                    ) : (
                                        <span>&mdash;</span>
                                    )}
                                </TitleText>
                            </Section>
                        </GroupSection>
                        <GroupSection>
                            <Section>
                                <Text
                                    variant="xsmallLight"
                                    className={classesText.gray2}
                                >
                                    Seat Office
                                </Text>
                                <TitleText variant="small">
                                    {user.seatOffice}
                                </TitleText>
                            </Section>
                        </GroupSection>

                        {user.departureDate && (
                            <GroupSection>
                                <Section>
                                    <Text
                                        variant="xsmallLight"
                                        className={classesText.gray2}
                                    >
                                        Departure Date
                                    </Text>
                                    <TitleText variant="small">
                                        {moment(user.departureDate).format(
                                            "MMM D, yyyy"
                                        )}
                                    </TitleText>
                                </Section>
                            </GroupSection>
                        )}
                        {user.talentManager ? (
                            <GroupSection
                                borderBottomNone={
                                    location.pathname !== "/admin/accessgroups"
                                }
                            >
                                <Card
                                    label="Talent Manager"
                                    photo={
                                        env.BESTWORK_REACT_ASSET_DOMAIN +
                                        "/" +
                                        user.talentManager.SmallPhotoUrl
                                    }
                                    name={user.talentManager.FullName}
                                    title={user.talentManager.Title}
                                    department={user.talentManager.Department}
                                    email={user.talentManager.Email}
                                />
                            </GroupSection>
                        ) : (
                            <></>
                        )}
                        {
                            <AccessGroupList
                                userId={userId}
                                departmentId={user.departmentId}
                                onClose={props.onClose}
                            />
                        }
                    </>
                ) : (
                    <Loader height="5rem" width="5rem" />
                )}
            </Body>
            <Footer>
                {canViewAllocations && (
                    <ButtonLink
                        href={`${globalThis.config.routes.allocation}?userId=${userId}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Button>
                            View Employee Allocations
                        </Button>
                    </ButtonLink>
                )}
                <Tooltip
                    placement="top"
                    title="Skill Search not available for Freelance"
                    show={user.type === "Freelancer"}
                >
                    <SkillSearchContainer>
                        <TextLink
                            href={profileLink}
                            target="_blank"
                            rel="noreferrer"
                            disabled={!profileLink}
                        >
                            <Text
                                className={!profileLink && "disabled"}
                                variant="xsmall"
                            >
                                Skill Search Profile
                            </Text>
                        </TextLink>
                    </SkillSearchContainer>
                </Tooltip>
            </Footer>
        </Container>
    );
};

EmployeeDrawer.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onClose: PropTypes.func,
};

export default EmployeeDrawer;
